import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { checkPermission } from '@/utils/permission';
import DynamicTable from '@/components/DynamicTable';
import { isValidDate, RenderCustomerServiceStatus } from '@/utils/helpers';
import { Button, Col, Modal, Row, Space, Tag, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { getSpecificCustomer } from '@/redux/actions/customerActions';
import HtmlParser from 'react-html-parser';
import NumberFormat from 'react-number-format';
import { getSpecificService, removeService } from '@/redux/actions/CustomerServicesActions';
import CustomerServicesDetail from '@/routes/customer-services/CustomerServicesDetail';
import CustomerServiceAddForm from '@/routes/customer-services/CustomerServiceAddForm';
import CustomerServiceEditForm from '@/routes/customer-services/CustomerServiceEditForm';
import PaymentHistory from '@/components/PaymentHistory';

const { confirm } = Modal;

const UsedServiceList = (props) => {

    const { customer = {} } = props;
    const services = customer.services_selected || [];

    const dispatch = useDispatch();
    const { id } = useParams();

    const allowCreate = checkPermission('customer_services', 'create');
    const allowDetail = checkPermission('customer_services', 'detail');
    const allowUpdate = checkPermission('customer_services', 'update');
    const allowDelete = checkPermission('customer_services', 'delete');
    const canViewFinance = checkPermission('customer', 'view_finance');

    const [isLoading, setIsLoading] = useState(false);
    const [isOpenAddForm, setIsOpenAddForm] = useState(false);
    const [isOpenEditForm, setIsOpenEditForm] = useState(false);
    const [currentCustomer, setCurrentCustomer] = useState({});
    const [currentUsedService, setCurrentUsedService] = useState({});
    const [isOpenDetail, setIsOpenDetail] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const openAddForm = async (open) => {
        await dispatch(getSpecificCustomer(id)).then((res) => {
            setCurrentCustomer(res);
        });
        setIsOpenAddForm(open);
    };

    const openEditForm = async (customerId, id) => {
        try {
            // Chạy song song 2 API
            const [serviceRes, customerRes] = await Promise.all([
                dispatch(getSpecificService(id)),
                dispatch(getSpecificCustomer(customerId)),
            ]);

            setCurrentUsedService(serviceRes);
            setCurrentCustomer(customerRes);
            setIsOpenEditForm(true);
        } catch (error) {
            console.error('Lỗi khi tải dữ liệu dịch vụ hoặc khách hàng:', error);
        }
    };


    const handleReloadData = async () => {
        try {
            setIsLoading(true);
            await dispatch(getSpecificCustomer(id));
            console.log('Reload customer data', id);
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to reload data:', error);
        }
    };

    const onDetail = async (id) => {
        setCurrentId(id);
        try {
            const res = await dispatch(getSpecificService(id));
            setCurrentUsedService(res);
        } catch (error) {
            console.error('Lỗi khi tải dịch vụ:', error);
        }
        setIsOpenDetail(true)
    };

    const openAlert = (id) => {
        confirm({
            title: 'Cảnh báo',
            content: 'Bạn chắc chắn muốn xoá bản ghi này?',
            okText: 'OK',
            cancelText: 'Huỷ',
            async onOk() {
                try {
                    await dispatch(removeService(id));
                    await handleReloadData();
                    Modal.destroyAll();
                } catch (error) {
                    console.error('Lỗi khi xoá dịch vụ:', error);
                }
            },
        });
    };

    const columns = [
        {
            title: 'Tên dịch vụ',
            dataIndex: 'service_name',
            key: 'service_name',
            width: '200px',
            sorter: false,
            render: (text, record) => {
                if (!text) {
                    return <Tag>-</Tag>;
                }

                const isTransferred = !!record.is_transferred;

                return (
                    <>
                        {allowDetail ? (
                            <Tooltip title="Xem chi tiết dịch vụ" placement="right">
                                <div
                                    className="color-primary item-action-btn"
                                    onClick={() => onDetail(record.id)}
                                >
                                    {text}
                                </div>
                            </Tooltip>
                        ) : (
                            <>{text}</>
                        )}

                        {record.converted_from_service_name && (
                            <Tooltip title={`Được chuyển từ: ${record.converted_from_service_name}`}>
                                <Tag color="green" className="ml-2">
                                    Được chuyển từ: {record.converted_from_service_name}
                                </Tag>
                            </Tooltip>
                        )}

                        {isTransferred && record.converted_to_service_name && (
                            <Tooltip title={`Đã chuyển sang: ${record.converted_to_service_name}`}>
                                <Tag color="red" className="ml-2">
                                    Đã chuyển sang: {record.converted_to_service_name}
                                </Tag>
                            </Tooltip>
                        )}
                    </>
                );
            },
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            sorter: false,
            width: '200px',
            render: (text, record) => {
                return (<>{HtmlParser(text)}</>)
            }
        },
        {
            title: "Giá bán",
            dataIndex: "sale_price",
            width: "120px",
            key: 'sale_price',
            sorter: false,
            render: (text, record) => {
                if (text) {
                    return (
                        <NumberFormat value={text} displayType={'text'} thousandSeparator={true} />
                    )
                }
            }
        },
        {
            title: "SL",
            dataIndex: "quantity",
            width: "40px",
            key: 'quantity',
            sorter: false,
            render: (text, record) => {
                return (
                    <NumberFormat value={text} displayType={'text'} thousandSeparator={true} />
                )
            }
        },
        {
            title: "Thành tiền",
            dataIndex: "amount",
            width: "120px",
            key: 'amount',
            sorter: false,
            render: (text, record) => {
                if (text) {
                    return (
                        <NumberFormat value={text} displayType={'text'} thousandSeparator={true} />
                    )
                }
            }
        },
        {
            title: 'Ngày chốt',
            dataIndex: 'selected_at',
            key: 'selected_at',
            width: '100px',
            render: (text, record) => {
                if (text) {
                    return (
                        <div>{isValidDate(text, false)}</div>
                    )
                }
            }
        },
        {
            title: 'Nhân viên tư vấn',
            dataIndex: 'consultant_directly',
            key: 'consultant_directly',
            width: 250,
            render: (_, record) => {
                const list = record.consultant_directly || [];
                if (list.length === 0) return null;

                return (
                    <div style={{ lineHeight: 1.3 }}>
                        {list.map(item => (
                            <div key={item.id}>
                                <span style={{ color: '#999' }}>{item.role}: </span>
                                <span style={{ fontWeight: 600 }}>{item.consultant_user}</span>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: 'Bác sĩ & Phụ tá',
            dataIndex: 'doctors',
            key: 'doctors',
            width: 220,
            render: (_, record) => {
                const visits = record.examination_visit || [];
                if (visits.length === 0) return null;

                // Lấy danh sách bác sĩ và phụ tá, loại bỏ trùng và null
                const doctors = [...new Set(
                    visits
                        .map((v) => v.doctor_charge_name || v.doctor_charge_id)
                        .filter(Boolean)
                )];

                const assistants = [...new Set(
                    visits
                        .map((v) => v.assistant_doctor_name || v.assistant_doctor_id)
                        .filter(Boolean)
                )];

                return (
                    <div style={{ lineHeight: 1.4 }}>
                        {doctors.length > 0 && (
                            <div>
                                <span style={{ color: '#999' }}>Bác sĩ: </span>
                                <span style={{ fontWeight: 600 }}>{doctors.join(', ')}</span>
                            </div>
                        )}
                        {assistants.length > 0 && (
                            <div>
                                <span style={{ color: '#999' }}>Phụ tá:</span>
                                <span style={{ fontWeight: 600 }}>{assistants.join(', ')}</span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            sorter: false,
            width: '100px',
            render: (text, record) => {
                if (text) {
                    return (
                        <div>{RenderCustomerServiceStatus(text)}</div>
                    )
                }
            }
        },
        {
            title: 'Actions',
            align: 'center',
            width: '180px',
            key: 'actions',
            render: (_, record) => {
                const isTransferred = !!record.is_transferred; // true nếu đã chuyển sang dịch vụ khác

                return (
                    <Space>
                        <Tooltip
                            title={
                                isTransferred
                                    ? 'Không thể sửa vì đã chuyển sang dịch vụ khác'
                                    : 'Chỉnh sửa dịch vụ'
                            }
                        >
                            <Button
                                size="small"
                                type="dashed"
                                className={`btn-edit ${isTransferred ? 'disabled-action' : ''}`}
                                icon={<EditOutlined />}
                                onClick={() => openEditForm(id, record.id)}
                                disabled={!allowUpdate || isTransferred}
                            >
                                Sửa
                            </Button>
                        </Tooltip>

                        {allowDelete && (
                            <Tooltip
                                title={
                                    isTransferred
                                        ? 'Không thể xóa vì đã chuyển sang dịch vụ khác'
                                        : 'Xóa dịch vụ khỏi khách hàng'
                                }
                            >
                                <Button
                                    size="small"
                                    type="dashed"
                                    className={`btn-delete ${isTransferred ? 'disabled-action' : ''}`}
                                    icon={<DeleteOutlined />}
                                    onClick={() => openAlert([record.id])}
                                    disabled={!allowDelete || isTransferred}
                                >
                                    Xóa
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        }
    ];

    const tables = <DynamicTable
        dataSource={services || []}
        columns={columns}
        isLoading={isLoading}
        pagination={false}
        rowSelection={false}
        moduleKey="used_services_list"
        buttonStyle={{ top: 6 }}
    />

    return (
        <div>
            <div className='mt-4 mb-4'>
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => openAddForm(true)}
                                disabled={!allowCreate}
                            >
                                Thêm dịch vụ chốt
                            </Button>
                            <Button
                                onClick={handleReloadData}
                                icon={<ReloadOutlined />}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>
            <div className='has-custom-column-table-btn'>{tables}</div>

            {canViewFinance && (
                <>
                    <h6 className='mt-4'>Lịch sử thanh toán</h6>
                    <PaymentHistory key={`payment_history-${customer?.id}`} data={customer?.cashReceipts} />
                </>
            )}

            <CustomerServiceAddForm
                visible={isOpenAddForm}
                currentCustomer={currentCustomer}
                reloadData={handleReloadData}
                onCancel={() => setIsOpenAddForm(false)}
            />
            <CustomerServiceEditForm
                visible={isOpenEditForm}
                currentData={currentUsedService}
                currentCustomer={currentCustomer}
                reloadData={handleReloadData}
                onCancel={() => setIsOpenEditForm(false)}
            />
            <CustomerServicesDetail
                visible={isOpenDetail}
                currentItem={currentUsedService}
                reloadData={() => onDetail(currentId)}
                onCancel={() => setIsOpenDetail(false)}
            />
        </div>
    );
};

export default UsedServiceList;
